// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import gql from 'graphql-tag'
import qs from 'query-string'
import moment from 'moment'

import {
  FilterSidebar,
  PicklistWithData,
  DynamicPicklist,
  EmployerBulkSMSMessages,
  DateRange
} from '../../'


const QUERY_EMPLOYER_BULKMESSAGE_AUDIENCES = gql`
  query {
    bulkmessageaudiences {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`


const getFiltersFromQueryString = (queryString) => {
  let queryParams = qs.parse(queryString)

  let filters = Object.assign(
    {},
    ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
      key === 'orderBy'
        ? { orderBy: queryParams[key] }
        : key === 'tab'
          ? { currentTab: parseInt(queryParams[key], 10) }
          : ['createdAtFrom', 'createdAtTo', 'scheduledAtFrom', 'scheduledAtTo'].indexOf(key) > -1
            ? { [`${key}Filter`]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
            : { [`${key}Filter`]: queryParams[key] }
    ))
  )
  return filters
}


class EmployerBulkSMSMessageList extends PureComponent {
  static propTypes = {
    employerId: PropTypes.string.isRequired
  }

  state = {
    audienceFilter: null,
    statusFilter: null,
    scheduledAtFromFilter: null,
    scheduledAtToFilter: null,
    createdAtFromFilter: null,
    createdAtToFilter: null,
    orderBy: '-created_at'
  }

  componentDidMount() {
    if (this.props.location.search.length === 0) {
      this.setQueryString(true)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      this.setState(getFiltersFromQueryString(this.props.location.search))
    }
  }

  setQueryString = (state, setDefault) => {
    const { orderBy, currentTab, statusFilter, audienceFilter, createdAtFromFilter, createdAtToFilter,
      scheduledAtFromFilter, scheduledAtToFilter} = this.state

    let filters = {
      orderBy: orderBy,
      tab: currentTab
    }

    if (statusFilter) {
      filters.status = statusFilter
    }

    if (audienceFilter) {
      filters.audience = audienceFilter
    }

    if (scheduledAtFromFilter) {
      filters.scheduledAtFrom = scheduledAtFromFilter.format('YYYYMMDDTHHmm')
    }
    if (scheduledAtToFilter) {
      filters.scheduledAtTo = scheduledAtToFilter.format('YYYYMMDDTHHmm')
    }

    if (createdAtFromFilter) {
      filters.createdAtFrom = createdAtFromFilter.format('YYYYMMDDTHHmm')
    }
    if (createdAtToFilter) {
      filters.createdAtTo = createdAtToFilter.format('YYYYMMDDTHHmm')
    }

    this.props.history.push({ search: qs.stringify(filters) })
  }

  changeOrderBy = (orderBy) => this.setState({ orderBy })

  getFilters = () => (
    [
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='BulkMessageStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter
            ? this.state.statusFilter
            : 'all'
          }
          onChange={status => (
            this.setState({
              statusFilter: status === 'all'
                ? null
                : status
            }, this.setQueryString)
          )}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Audience',
        options: <DynamicPicklist
          id='audience'
          query={QUERY_EMPLOYER_BULKMESSAGE_AUDIENCES}
          field='name'
          defaultValue='All'
          value={this.state.audience}
          onChange={audience => (
            this.setState({
              audienceFilter: audience === 'All'
                ? ''
                : audience
            }, this.setQueryString)
          )}
          useIds={true}
        />
      },
      {
        title: 'Created between',
        options: <DateRange
          startDate={this.state.createdAtFromFilter}
          endDate={this.state.createdAtToFilter}
          onStartDateChange={date => this.setState({ createdAtFromFilter: date }, this.setQueryString)}
          onEndDateChange={date => this.setState({ createdAtToFilter: date }, this.setQueryString)}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      },
      {
        title: 'Scheduled between',
        options: <DateRange
          startDate={this.state.scheduledAtFromFilter}
          endDate={this.state.scheduledAtToFilter}
          onStartDateChange={date => this.setState({ scheduledAtFromFilter: date }, this.setQueryString)}
          onEndDateChange={date => this.setState({ scheduledAtToFilter: date }, this.setQueryString)}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]
  )

  render() {
    const { employerId } = this.props
    const { statusFilter, audienceFilter, createdAtFromFilter, createdAtToFilter, scheduledAtFromFilter, scheduledAtToFilter, orderBy } = this.state

    const filters = this.getFilters()

    return (
      <Fragment>
        <FilterSidebar
          filters={filters}
          filtersApplied={[
              statusFilter || '',
              audienceFilter || '',
              createdAtFromFilter || createdAtToFilter
                ? createdAtFromFilter
                  ? createdAtFromFilter.toString()
                  : createdAtToFilter.toString()
                : '',
              scheduledAtFromFilter || scheduledAtToFilter
                ? scheduledAtFromFilter
                  ? scheduledAtFromFilter.toString()
                  : scheduledAtToFilter.toString()
                : ''
            ].filter(filter => filter.length > 0).length
          }
          onClear={() => this.setState({
            statusFilter: null,
            audienceFilter: null,
            createdAtFromFilter: null,
            createdAtToFilter: null,
            scheduledAtFromFilter: null,
            scheduledAtToFilter: null
          }, this.setQueryString)}
          key={filters}
        />
        <div className='slds-col slds-size_10-of-12 slds-scrollable_x slds-border_top'>
          <EmployerBulkSMSMessages
            employerId={employerId}
            status={statusFilter}
            audience={audienceFilter}
            createdAtFrom={createdAtFromFilter}
            createdAtTo={createdAtToFilter}
            scheduledAtFrom={scheduledAtFromFilter}
            scheduledAtTo={scheduledAtToFilter}
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
          />
        </div>
      </Fragment>
    )
  }
}

export default withRouter(EmployerBulkSMSMessageList)



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-bulk-sms-message-list/index.js